import { useEffect, useState } from "react";
import { useTranslation } from "_metronic/i18n/language";
import { useThemeApp } from "_metronic/materialUIThemeProvider/ThemeProvider";
import { AnswerType, FileType, QuestionType, QuizType } from "app/types";
import { QUIZ_TYPES_CONTANTS } from "app/types/quiz-constants";
import { selectInput, SelectInputType } from "mocks/quizInputs";
import { defaultValues } from "./update-data";
import Swal from "sweetalert2";
import { addUpdate } from "app/services/uploadFileToAzureBlob";
import { useFieldArray, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { schema } from "./update-schema";
import { QuestionFormType } from "./update-type";
import { showMensage } from "app/services/FeaturesElement";
import { getFilesByFileOwnerId } from "../components/ModalAddImageAnswer";
import { QuestionApiServiceImpl } from "app/services";

interface AnswerFile {
  answer: AnswerType;
  file?: FileType;
}

type QuestionFormModelType = {
  quiz: QuizType;
  question?: QuestionType;
  handleClose: () => void;
  onFinish: () => void;
  open?: boolean;
  setQuestion?: (question: QuestionType) => void;
  returnInArray?: boolean;
};

export const useQuestionForm = ({
  quiz,
  returnInArray = false,
  open = false,
  question,
  handleClose,
  onFinish,
  setQuestion,
}: QuestionFormModelType) => {
  const [loading, setLoading] = useState(false);
  const [modalAnswersImage, setModalAnswersImage] = useState(false);
  const [answersOld, setAnswersOld] = useState<AnswerType[]>([]);
  const [imageQuestion, setImageQuestion] = useState<FileType>({} as FileType);
  const [preViewImage, setPreViewImage] = useState("");

  const { isDarkMode } = useThemeApp();
  const translate = useTranslation();
  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<QuestionFormType>({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "answers",
  });
  const questions = quiz.questions || [];
  const styleInput = {
    backgroundColor: isDarkMode ? "transparent" : "#fff",
    color: isDarkMode ? "#ffffff" : "#747a91",
  };
  useEffect(() => {
    setValue("quizTypeKey", quiz.type);
    if (question) {
      setValue("quizId", question.quizId || quiz.quizId);
      setValue("quizQuestionId", question.quizQuestionId);
      setValue("questionId", question.questionId);
      setValue("quest", question?.question?.quest || "");
      setValue("description", question.question?.description || "");
      setValue("fragranceId", question.fragranceId);
      setValue("rightAnswer", question.rightAnswer);
      setValue("instruction", question?.instruction || "");
      if (question.files?.length && question.files[0]?.tempUri) {
        setImageQuestion(question.files?.length ? question.files[0] : {});
        setPreViewImage(question?.files[0]?.tempUri);
      }
      setValue("type", question.type);
      setValue(
        "categoryCognitiveQuestionId",
        question?.categoryCognitiveQuestionId || "",
      );
      setValue("arrangeQuestionSequence", question.arrangeQuestionSequence);
      setValue("timeInSeconds", question.timeInSeconds);
      setAnswersOld(question.answers);
      replace(
        question.answers.map(answer => ({
          quizQuestionAnswerId: answer.quizQuestionAnswerId,
          answerId: answer.answerId,
          desc: answer.desc || answer.answer?.label || "",
          description: answer.description || answer.answer?.description || "",
          nextQuizQuestionId: answer.nextQuizQuestionId || 0,
        })),
      );
    } else {
      setValue("arrangeQuestionSequence", questions.length + 1);
      if (+quiz.type === QUIZ_TYPES_CONTANTS.AUDIOMETRY) {
        setValue("type", "checkbox");
      }
    }

    // Efeito de limpeza para liberar URLs de objetos quando o componente for desmontado
    return () => {
      if (preViewImage && preViewImage.startsWith("blob:")) {
        URL.revokeObjectURL(preViewImage);
      }
    };
  }, [question, setValue, replace, open, quiz.quizId]);
  const clearForm = () => {
    // Limpar o URL do objeto anterior para evitar vazamentos de memória
    if (preViewImage && preViewImage.startsWith("blob:")) {
      URL.revokeObjectURL(preViewImage);
    }

    reset(defaultValues);
    setImageQuestion({} as FileType);
    setPreViewImage("");
    setValue("arrangeQuestionSequence", questions.length + 1);

    // Reset answers
    replace([
      {
        quizQuestionAnswerId: 0,
        answerId: 0,
        desc: "",
        description: "",
        nextQuizQuestionId: 0,
      },
    ]);

    if (+quiz.type === QUIZ_TYPES_CONTANTS.AUDIOMETRY) {
      setValue("type", "checkbox");
    }

    setValue("quizTypeKey", quiz.type);
    setValue("quizId", quiz.quizId);
  };

  const onClose = () => {
    handleClose();
    setModalAnswersImage(false);
    clearForm();
    onFinish();
  };

  const onSubmit = async (data: QuestionType) => {
    setLoading(true);
    try {
      const newData = {
        ...data,
        question: { quest: data.quest, description: data.description || "" },
        quizId: data.quizId || question?.quizId || quiz.quizId,
        file: imageQuestion,
        quizTypeKey: undefined,
      };
      delete newData.quizTypeKey;

      const rightAnswers = data.rightAnswer?.split(";") || [];
      if (
        +quiz.type === QUIZ_TYPES_CONTANTS.AUDIOMETRY &&
        (rightAnswers.length < 3 || data.answers.length < 9)
      ) {
        showMensage(
          translate,
          null,
          data.answers.length < 9
            ? translate("screens_quiz_labels_audiometry_error_answer_count")
            : translate(
                "screens_quiz_labels_audiometry_error_right_answer_count",
              ),
        );
        return;
      }

      newData.questionId = returnInArray
        ? question?.questionId || data.questionId || 0
        : 0;

      let newAnswersFile: AnswerFile[] = await Promise.all(
        newData.answers.map(async answer => {
          const newFile = await getFilesByFileOwnerId(
            answer.quizQuestionAnswerId.toString(),
          );

          const newAnswerFile: AnswerFile = {
            answer,
            file: newFile,
          };

          return newAnswerFile;
        }),
      );

      newData.answers = newData.answers.map((answer, index) => {
        const originalAnswer = question?.answers?.[index]?.answer;
        const answerFile =
          newAnswersFile[index]?.file || originalAnswer?.files?.[0];

        return {
          ...answer,
          answerId: 0,
          quizQuestionAnswerId: 0,
          answer:
            originalAnswer || answerFile
              ? {
                  ...(originalAnswer || {}),
                  label: answer.desc,
                  desc: answer.desc,
                  description: answer.description,
                  files: answerFile ? [answerFile] : originalAnswer?.files,
                }
              : undefined,
        };
      });

      if (!returnInArray) {
        const newQuestion: QuestionType = await addUpdate(newData);
        newAnswersFile = newAnswersFile
          .map(answerFile => {
            const newAnswer = newQuestion.answers.find(
              answer => answer.desc === answerFile.answer.desc,
            );

            if (!newAnswer) {
              return undefined;
            }

            return {
              answer: newAnswer,
              file: answerFile.file,
            };
          })
          .filter(answerFile => answerFile !== undefined);

        handleSubmitImagesAnswers(newAnswersFile);
      } else {
        setQuestion && setQuestion(newData);
      }

      const message = data.quizQuestionId
        ? translate("registration_updated_successfully")
        : translate("crudMessages_successText");
      clearForm();

      // Only close if not an image question
      if (data.type !== "image") {
        onClose();
      }

      Swal.fire({
        title: translate("defaultMessages_success"),
        text: message,
        icon: "success",
      }).then(() => {
        if (data.type === "image") {
          setModalAnswersImage(true);
        }
      });
    } catch (error) {
      handleClose();
      Swal.fire({
        title: translate("screenApp_capsula_error"),
        text: "Não foi possivel add ou atualiza perguta",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleFileQuestion = (event: { target: any }) => {
    const file: File | undefined = event?.target.files?.[0];

    // Limpar o URL do objeto anterior para evitar vazamentos de memória
    if (preViewImage && preViewImage.startsWith("blob:")) {
      URL.revokeObjectURL(preViewImage);
    }

    if (file) {
      const prepareData: FileType = {
        file,
        name: file.name,
      };
      setImageQuestion(prepareData);

      // Cria um novo URL de objeto para a visualização
      const objectUrl = URL.createObjectURL(file);
      setPreViewImage(objectUrl);
    }
  };

  const handleClearQuest = () => {
    setValue("quest", "");
    setValue("questionId", 0);
  };

  const handleQuestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("quest", e.target.value);
    if (!e.target.value) setValue("questionId", 0);
  };

  const handleDescChange = (index: number, value: string): void => {
    setValue(`answers.${index}.desc`, value);
    if (value === "") {
      setValue(`answers.${index}.answerId`, 0);
    }
  };
  const clearImageQuestion = () => {
    // Limpar o URL do objeto anterior para evitar vazamentos de memória
    if (preViewImage && preViewImage.startsWith("blob:")) {
      URL.revokeObjectURL(preViewImage);
    }

    reset(defaultValues);
    setImageQuestion({} as FileType);
    setPreViewImage("");
  };

  const showModalAnswersImage = () => setModalAnswersImage(true);

  function getCognitiveInputs(quizType: number) {
    return selectInput.filter(value => {
      const isCognitiveQuiz = quizType === QUIZ_TYPES_CONTANTS.COGNITIVE;
      const excludedTypes = ["number", "voice", "speechtotext", "drawing"];
      if (!isCognitiveQuiz && excludedTypes.includes(value.type)) {
        return false;
      }
      return true;
    });
  }

  const questionService = new QuestionApiServiceImpl();

  const handleSubmitImagesAnswers = async (answersFile: AnswerFile[]) => {
    setLoading(true);
    try {
      for (const answerFile of answersFile) {
        const file = answerFile.file;
        const fileSource = await convertUrlToFile(
          answerFile.file?.tempUri || "",
          answerFile.file?.name || "",
        );
        // Se não houver novo arquivo selecionado, pular o envio
        if (!file) {
          continue;
        }

        const fileOwnerId = answerFile.answer.quizQuestionAnswerId.toString();
        const formData = new FormData();

        if (fileSource) {
          formData.append("file", fileSource);
        } else if (file.fileId && file.tempUri) {
          formData.append("fileId", file.fileId.toString());
          formData.append("tempUri", file.tempUri);
          formData.append("path", file.path || "");
          formData.append("downloads", file.downloads?.toString() || "0");
        }

        formData.append("fileOwnerId", fileOwnerId);

        try {
          // Método para cadastrar o arquivo
          const reqBase = { method: "POST", url: "questionanswerfile" };

          await questionService.makeHttpRequest({
            ...reqBase,
            data: formData,
          });

          console.log(
            `File uploaded successfully for answer ${answerFile.answer.quizQuestionAnswerId}`,
          );
        } catch (error) {
          console.error(
            `Error uploading file for answer ${answerFile.answer.quizQuestionAnswerId}:`,
            error,
          );
        }
      }
    } catch (error) {
      handleClose();
      Swal.fire({
        title: translate("screenApp_capsula_error"),
        text: "Não foi possivel add ou imagens da reposta",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const convertUrlToFile = async (
    url: string,
    fileName: string,
  ): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();

    const mimeType = blob.type || "image/png";

    return new File([blob], fileName, { type: mimeType });
  };
  return {
    cognitiveInputs: getCognitiveInputs(+quiz.type),
    sequence: watch("arrangeQuestionSequence") || 0,
    selectedType: watch("type") as SelectInputType,
    onSubmit: handleSubmit(onSubmit),
    answers: watch("answers"),
    styleInput,
    translate,
    showModalAnswersImage,
    remove,
    register,
    preViewImage,
    onClose,
    handleQuestChange,
    handleFileQuestion,
    handleDescChange,
    handleClearQuest,
    fields,
    errors,
    control,
    setValue,
    watch,
    clearImageQuestion,
    clearForm,
    append,
    modalAnswersImage,
    answersOld,
    loading,
  };
};
